import { cn } from "@/utils";
import { Search, X } from "lucide-react";
import { useRef, useState } from "react";
import { WIDGET_PRESETS, type WidgetPreset } from "../nodes/widgets";
import { StickerArt } from "../nodes/widgets/StickerArt";
import { matchesWidgetQuery } from "./toolbar/tray/widgetSearch";

// Grid space inside the 480px popover: 464px across after p-2, gap-2.
const SECTIONS: {
  title: string;
  presets: WidgetPreset[];
  cols: number;
  previewH: number;
}[] = [
  {
    title: "Tools",
    presets: WIDGET_PRESETS.filter((p) => !p.family),
    cols: 2,
    previewH: 176,
  },
  {
    title: "Status",
    presets: WIDGET_PRESETS.filter((p) => p.family === "status"),
    cols: 3,
    previewH: 80,
  },
  {
    title: "Jargon",
    presets: WIDGET_PRESETS.filter((p) => p.family === "jargon"),
    cols: 4,
    previewH: 96,
  },
  {
    title: "Callouts",
    presets: WIDGET_PRESETS.filter((p) => p.family === "callout"),
    cols: 4,
    previewH: 80,
  },
];

export { matchesWidgetQuery } from "./toolbar/tray/widgetSearch";

interface CanvasWidgetsPopoverProps {
  onAddWidget: (preset: WidgetPreset) => void;
}

export function CanvasWidgetsPopover({
  onAddWidget,
}: CanvasWidgetsPopoverProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const sections = SECTIONS.map((s) => ({
    ...s,
    presets: s.presets.filter((p) => matchesWidgetQuery(p, searchQuery)),
  })).filter((s) => s.presets.length > 0);

  return (
    <div
      className="z-20 w-[480px] max-w-[calc(100vw-2rem)]"
      data-canvas-control
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex h-[460px] flex-col overflow-hidden rounded-xl border border-n-10 bg-n-00 text-n-90 shadow-elev-5">
        <div className="flex shrink-0 items-center gap-2 border-b border-n-10 px-3">
          <Search className="size-4 shrink-0 text-n-50" />
          <input
            type="text"
            placeholder="Search widgets"
            aria-label="Search widgets"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            autoFocus
            className="flex h-11 w-full bg-transparent text-sm outline-none placeholder:text-n-50"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
              className="shrink-0 rounded-sm p-1 text-n-50 hover:bg-n-10 hover:text-n-90"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {sections.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-n-50">No widgets found</p>
            </div>
          ) : (
            sections.map((section) => (
              <section key={section.title}>
                <h3 className="px-1 pb-1.5 pt-3 text-xs font-medium text-n-60">
                  {section.title}
                </h3>
                <div
                  className="grid gap-2"
                  style={{
                    gridTemplateColumns: `repeat(${section.cols}, minmax(0, 1fr))`,
                  }}
                >
                  {section.presets.map((preset) => (
                    <WidgetTile
                      key={preset.id}
                      preset={preset}
                      previewW={(464 - 8 * (section.cols - 1)) / section.cols}
                      previewH={section.previewH}
                      // The popover stays open so several widgets can be placed.
                      onSelect={() => onAddWidget(preset)}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/** The same art or face the widget renders on the canvas. */
function WidgetArt({ preset }: { preset: WidgetPreset }) {
  if (preset.html)
    return <StickerArt markup={preset.html} scope={`pick-${preset.id}`} />;
  const Preview = preset.preview;
  return Preview ? <Preview /> : null;
}

interface WidgetTileProps {
  preset: WidgetPreset;
  previewW: number;
  previewH: number;
  onSelect: () => void;
}

function WidgetTile({ preset, previewW, previewH, onSelect }: WidgetTileProps) {
  const artRef = useRef<HTMLDivElement>(null);
  const inset = 12;
  const scale = Math.min(
    (previewW - inset) / preset.defaultWidth,
    (previewH - inset) / preset.defaultHeight,
  );

  return (
    // Not a <button>: the timer's face has buttons of its own.
    <div
      role="button"
      tabIndex={0}
      aria-label={preset.label}
      title={preset.meaning ? `${preset.label}: ${preset.meaning}` : undefined}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        onSelect();
      }}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("application/widget-preset", preset.id);
        e.dataTransfer.effectAllowed = "move";
        const art = artRef.current;
        if (!art) return;
        const ghostScale = Math.min(0.5, 200 / preset.defaultWidth);
        const ghost = document.createElement("div");
        ghost.style.cssText = `position:fixed;top:-9999px;left:-9999px;pointer-events:none;width:${preset.defaultWidth * ghostScale}px;height:${preset.defaultHeight * ghostScale}px;`;
        const clone = art.cloneNode(true) as HTMLElement;
        clone.style.transform = `scale(${ghostScale})`;
        clone.style.transformOrigin = "top left";
        ghost.appendChild(clone);
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, 0, 0);
        requestAnimationFrame(() => ghost.remove());
      }}
      className={cn(
        "group flex cursor-grab flex-col rounded-lg p-1 outline-none active:cursor-grabbing",
        "hover:bg-n-10 focus-visible:ring-2 focus-visible:ring-canvas-selection/60",
      )}
    >
      <div
        className="flex items-center justify-center overflow-hidden rounded-md bg-n-05"
        style={{ height: previewH }}
      >
        <div
          ref={artRef}
          inert
          className="pointer-events-none shrink-0"
          style={{
            width: preset.defaultWidth,
            height: preset.defaultHeight,
            transform: `scale(${scale})`,
          }}
        >
          <WidgetArt preset={preset} />
        </div>
      </div>
      <span className="truncate px-1 pb-0.5 pt-1.5 text-xs text-n-70 group-hover:text-n-90">
        {preset.label}
      </span>
    </div>
  );
}
